import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Download, Trash2, Share2, Loader2, ChevronLeft, ChevronRight, FileArchive, FileText, Edit, Sparkles, Pencil } from 'lucide-react';
import axios from 'axios';
import JSZip from 'jszip';
import { jsPDF } from 'jspdf';
import { getMediaUrl } from '../utils/mediaUrl';
import SlideCanvasPreview from '../components/SlideCanvasPreview';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const CarouselDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [carousel, setCarousel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSlide, setSelectedSlide] = useState(0);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [previewMode, setPreviewMode] = useState('original');
  const previewContainerRef = useRef(null);

  useEffect(() => {
    fetchCarousel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Close download menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDownloadMenu && !event.target.closest('.download-menu-container')) {
        setShowDownloadMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDownloadMenu]);

  // Auto-switch to edited mode if there are edited slides
  useEffect(() => {
    if (carousel) {
      const hasEdited = carousel.slides?.some(s => s.edited_image_url || s.slide_data?.elements?.length > 0);
      if (hasEdited) setPreviewMode('edited');
    }
  }, [carousel]);

  const fetchCarousel = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      
      if (!token) {
        setError('Please log in to view this carousel');
        setLoading(false);
        return;
      }

      console.log('🔍 Fetching carousel:', id);

      const response = await axios.get(`${API_BASE_URL}/carousels/${id}/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('✅ Carousel fetched:', response.data);
      setCarousel(response.data);
      setLoading(false);
    } catch (err) {
      console.error('❌ Error fetching carousel:', err);
      setError(err.response?.data?.detail || 'Failed to load carousel');
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this carousel?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      
      await axios.delete(`${API_BASE_URL}/carousels/${id}/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('✅ Carousel deleted');
      navigate('/dashboard');
    } catch (err) {
      console.error('❌ Error deleting carousel:', err);
      alert('Failed to delete carousel');
    }
  };

  const handlePrevSlide = () => {
    setSelectedSlide(prev => (prev > 0 ? prev - 1 : carousel.slides.length - 1));
  };

  const handleNextSlide = () => {
    setSelectedSlide(prev => (prev < carousel.slides.length - 1 ? prev + 1 : 0));
  };

  // Note: renderSlideToDataURL function removed - now using backend rendering via edited_image_url

  const downloadAsZip = async () => {
    try {
      setDownloadingZip(true);
      setShowDownloadMenu(false);
      console.log('📦 Starting ZIP download...');

      const zip = new JSZip();
      const folder = zip.folder(carousel.title || 'carousel');

      // Fetch all images and add to ZIP
      for (let i = 0; i < carousel.slides.length; i++) {
        const slide = carousel.slides[i];
        
        console.log(`📥 Processing slide ${i + 1}...`);
        
        // Use edited_image_url if available, otherwise use original image_url
        const imageUrl = slide.edited_image_url || slide.image_url;
        
        if (!imageUrl) {
          console.warn(`⚠️ Slide ${i + 1} has no image, skipping...`);
          continue;
        }
        
        const fullImageUrl = imageUrl.startsWith('http') 
          ? imageUrl 
          : getMediaUrl(imageUrl);
        
        const response = await fetch(fullImageUrl);
        const blob = await response.blob();
        
        // Use .png extension for edited slides, preserve original for AI-generated
        const extension = slide.edited_image_url ? 'png' : (slide.image_url.split('.').pop() || 'jpg');
        const filename = `slide-${i + 1}.${extension}`;
        
        folder.file(filename, blob);
      }

      console.log('🗜️ Generating ZIP file...');
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Download ZIP
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `${carousel.title || 'carousel'}.zip`;
      link.click();
      
      console.log('✅ ZIP download complete!');
    } catch (error) {
      console.error('❌ ZIP download failed:', error);
      alert('Failed to download ZIP. Please try again.');
    } finally {
      setDownloadingZip(false);
    }
  };

  const downloadAsPdf = async () => {
    try {
      setDownloadingPdf(true);
      setShowDownloadMenu(false);
      console.log('📄 Starting PDF generation...');

      // Get carousel dimensions
      const width = carousel.width || 1080;
      const height = carousel.height || 1080;
      
      // Create PDF with carousel dimensions (convert px to mm)
      const pdfWidth = width * 0.264583; // px to mm
      const pdfHeight = height * 0.264583;
      
      const pdf = new jsPDF({
        orientation: width > height ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [pdfWidth, pdfHeight]
      });

      // Add each slide to PDF
      for (let i = 0; i < carousel.slides.length; i++) {
        const slide = carousel.slides[i];

        console.log(`📥 Adding slide ${i + 1} to PDF...`);
        
        // Use edited_image_url if available, otherwise use original image_url
        const imageUrl = slide.edited_image_url || slide.image_url;
        
        if (!imageUrl) {
          console.warn(`⚠️ Slide ${i + 1} has no image, skipping...`);
          continue;
        }
        
        const fullImageUrl = imageUrl.startsWith('http') 
          ? imageUrl 
          : getMediaUrl(imageUrl);
        
        // Fetch image
        const response = await fetch(fullImageUrl);
        const blob = await response.blob();
        
        // Convert to base64
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });

        // Add new page for each slide (except first)
        if (i > 0) {
          pdf.addPage([pdfWidth, pdfHeight]);
        }
        
        // Add image to PDF
        pdf.addImage(base64, 'PNG', 0, 0, pdfWidth, pdfHeight);
      }

      console.log('💾 Saving PDF...');
      pdf.save(`${carousel.title || 'carousel'}.pdf`);
      
      console.log('✅ PDF download complete!');
    } catch (error) {
      console.error('❌ PDF generation failed:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const getPlatformName = (platform) => {
    const platforms = {
      instagram_square: 'Instagram Square',
      instagram_story: 'Instagram Story',
      instagram_feed: 'Instagram Feed',
      linkedin_post: 'LinkedIn Post',
      facebook_post: 'Facebook Post',
      twitter_post: 'Twitter Post',
    };
    return platforms[platform] || platform;
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-yellow-100 text-yellow-800',
      published: 'bg-green-100 text-green-800',
      archived: 'bg-gray-100 text-gray-800',
    };

    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-2 text-gray-600">Loading carousel...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card p-8 text-center">
          <div className="text-red-600 mb-4">
            <p className="text-lg font-semibold">Error Loading Carousel</p>
            <p className="text-sm mt-2">{error}</p>
          </div>
          <Link to="/dashboard" className="btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (!carousel) {
    return null;
  }

  const currentSlide = carousel.slides?.[selectedSlide];
  const hasSlides = carousel.slides && carousel.slides.length > 0;
  const hasEditedSlides = carousel.slides?.some(s => s.edited_image_url || s.slide_data?.elements?.length > 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <Link 
          to="/dashboard" 
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Link>
        
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{carousel.title}</h1>
            {carousel.description && (
              <p className="text-gray-600 mt-2">{carousel.description}</p>
            )}
            <div className="flex items-center space-x-4 mt-4">
              <span className="text-sm text-gray-600">
                {getPlatformName(carousel.platform)}
              </span>
              <span className="text-sm text-gray-600">
                {carousel.width} × {carousel.height}
              </span>
              <span className="text-sm text-gray-600">
                {carousel.slide_count || carousel.slides?.length || 0} slides
              </span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="relative">
              {/* Glowing background effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg blur-lg opacity-75 group-hover:opacity-100 transition-opacity duration-300 animate-pulse-slow"></div>
              
              <Link 
                to={`/editor/${carousel.id}`}
                className="btn-primary relative flex items-center space-x-2 group shadow-2xl hover:shadow-purple-500/25 transform hover:scale-110 transition-all duration-300 animate-pulse-subtle border-2 border-yellow-400/50 hover:border-yellow-300 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                {/* Animated sparkles around the button */}
                <div className="absolute -top-2 -left-2 w-2 h-2 bg-yellow-300 rounded-full animate-bounce delay-100"></div>
                <div className="absolute -top-1 -right-3 w-1 h-1 bg-pink-300 rounded-full animate-bounce delay-300"></div>
                <div className="absolute -bottom-2 -left-3 w-1.5 h-1.5 bg-blue-300 rounded-full animate-bounce delay-500"></div>
                <div className="absolute -bottom-1 -right-2 w-2 h-2 bg-green-300 rounded-full animate-bounce delay-700"></div>
                
                <Sparkles className="h-5 w-5 text-yellow-300 group-hover:rotate-180 transition-transform duration-500 drop-shadow-lg" />
                <span className="font-bold text-white drop-shadow-md">Edit in Editor</span>
                
                {/* Enhanced notification badge */}
                <div className="absolute -top-2 -right-2 flex">
                  <div className="w-4 h-4 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full animate-ping"></div>
                  <div className="w-4 h-4 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full relative flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
                
                {/* Shimmer effect overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 group-hover:translate-x-full transition-transform duration-1000 ease-out"></div>
              </Link>
              
              {/* Floating text label */}
              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs px-3 py-1 rounded-full font-semibold shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                ✨ Create Amazing Content
              </div>
            </div>

            {/* Download Dropdown */}
            <div className="relative download-menu-container">
              <button 
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                disabled={downloadingZip || downloadingPdf}
                className="btn-secondary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {(downloadingZip || downloadingPdf) ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{downloadingZip ? 'Creating ZIP...' : 'Creating PDF...'}</span>
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    <span>Download</span>
                  </>
                )}
              </button>
              
              {/* Download Menu */}
              {showDownloadMenu && !downloadingZip && !downloadingPdf && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                  <div className="py-1">
                    <button
                      onClick={downloadAsZip}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors"
                    >
                      <FileArchive className="h-5 w-5 text-blue-600" />
                      <div>
                        <div className="font-medium text-gray-900">Download as ZIP</div>
                        <div className="text-xs text-gray-500">All slides in a zip file</div>
                      </div>
                    </button>
                    <button
                      onClick={downloadAsPdf}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors border-t border-gray-100"
                    >
                      <FileText className="h-5 w-5 text-red-600" />
                      <div>
                        <div className="font-medium text-gray-900">Download as PDF</div>
                        <div className="text-xs text-gray-500">Combined PDF document</div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button 
              onClick={handleDelete}
              className="btn-secondary text-red-600 hover:bg-red-50 flex items-center space-x-2"
            >
              <Trash2 className="h-4 w-4" />
              <span>Delete</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Slide Preview */}
        <div className="lg:col-span-2">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Slide Preview</h2>
              <div className="flex items-center bg-gray-100 border border-gray-200 rounded-xl p-1 gap-1">
                <button
                  onClick={() => setPreviewMode('original')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    previewMode === 'original'
                      ? 'bg-white text-primary-700 shadow-sm border border-gray-200'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-white/60'
                  }`}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  AI Generated
                </button>
                <button
                  onClick={() => setPreviewMode('edited')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    previewMode === 'edited'
                      ? 'bg-white text-primary-700 shadow-sm border border-gray-200'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-white/60'
                  }`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edited
                  {hasEditedSlides && (
                    <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                  )}
                </button>
              </div>
            </div>
            
            {hasSlides ? (
              <div>
                {/* Main Slide Display */}
                <div
                  ref={previewContainerRef}
                  className="relative bg-gray-100 rounded-lg overflow-hidden mb-4 flex justify-center py-2"
                >
                  {previewMode === 'original' ? (
                    currentSlide?.image_url ? (
                      <img
                        src={currentSlide.image_url.startsWith('http')
                          ? currentSlide.image_url
                          : `${API_BASE_URL.replace('/api', '')}${currentSlide.image_url}`}
                        alt={`Slide ${selectedSlide + 1}`}
                        style={{
                          width: '500px',
                          height: `${500 * (carousel.height / carousel.width)}px`,
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          console.error('❌ Image failed to load:', currentSlide.image_url);
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400"><rect fill="%23f3f4f6"/><text x="50%" y="50%" text-anchor="middle" fill="%236b7280" font-size="16">Image not available</text></svg>';
                        }}
                      />
                    ) : (
                      <div className="flex items-center justify-center" style={{ width: '500px', height: '500px' }}>
                        <p className="text-gray-500">No image available</p>
                      </div>
                    )
                  ) : (
                    currentSlide?.edited_image_url ? (
                      <img
                        src={currentSlide.edited_image_url}
                        alt={`Slide ${selectedSlide + 1} (edited)`}
                        style={{
                          width: '500px',
                          height: `${500 * (carousel.height / carousel.width)}px`,
                          objectFit: 'contain'
                        }}
                        onError={(e) => { e.target.src = currentSlide.image_url || ''; }}
                      />
                    ) : (
                      <SlideCanvasPreview
                        slideData={currentSlide?.slide_data}
                        canvasWidth={carousel.width || 1080}
                        canvasHeight={carousel.height || 1080}
                        previewWidth={500}
                      />
                    )
                  )}
                  
                  {/* Navigation Arrows */}
                  {carousel.slides.length > 1 && (
                    <>
                      <button
                        onClick={handlePrevSlide}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg"
                      >
                        <ChevronLeft className="h-6 w-6 text-gray-800" />
                      </button>
                      <button
                        onClick={handleNextSlide}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg"
                      >
                        <ChevronRight className="h-6 w-6 text-gray-800" />
                      </button>
                    </>
                  )}
                  
                  {/* Slide Counter */}
                  <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                    {selectedSlide + 1} / {carousel.slides.length}
                  </div>
                </div>
                
                {/* Slide Info */}
                {currentSlide && (
                  <div className="space-y-2">
                    {currentSlide.title && (
                      <h3 className="font-semibold text-gray-900">{currentSlide.title}</h3>
                    )}
                    {currentSlide.description && (
                      <p className="text-gray-600 text-sm">{currentSlide.description}</p>
                    )}
                    {currentSlide.generation_status && (
                      <div className="flex items-center space-x-2 text-sm">
                        <span className="text-gray-500">Status:</span>
                        <span className={`font-medium ${
                          currentSlide.generation_status === 'success' ? 'text-green-600' :
                          currentSlide.generation_status === 'failed' ? 'text-red-600' :
                          'text-yellow-600'
                        }`}>
                          {currentSlide.generation_status}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">No slides available</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Slide Thumbnails */}
          {hasSlides && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">All Slides</h3>
                <span className="text-xs text-gray-400">
                  {previewMode === 'edited' ? 'Edited' : 'AI Generated'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {carousel.slides.map((slide, idx) => (
                  <button
                    key={slide.id || idx}
                    onClick={() => setSelectedSlide(idx)}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      selectedSlide === idx
                        ? 'border-primary-600 ring-2 ring-primary-200'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {previewMode === 'edited' ? (
                      slide.edited_image_url ? (
                        <img
                          src={slide.edited_image_url}
                          alt={`Slide ${idx + 1} (edited)`}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = slide.image_url || ''; }}
                        />
                      ) : (
                        <div className="w-full h-full overflow-hidden flex items-center justify-center bg-gray-50">
                          <SlideCanvasPreview
                            slideData={slide.slide_data}
                            canvasWidth={carousel.width || 1080}
                            canvasHeight={carousel.height || 1080}
                            previewWidth={110}
                          />
                        </div>
                      )
                    ) : (
                      slide.image_url ? (
                        <img
                          src={slide.image_url.startsWith('http')
                            ? slide.image_url
                            : `${API_BASE_URL.replace('/api', '')}${slide.image_url}`}
                          alt={`Slide ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <span className="text-gray-400 text-xs">No image</span>
                        </div>
                      )
                    )}
                    <div className="absolute bottom-1 right-1 bg-black/70 text-white px-2 py-0.5 rounded text-xs">
                      {idx + 1}
                    </div>
                    {previewMode === 'original' && slide.slide_data?.elements?.length > 0 && (
                      <div className="absolute top-1 left-1 bg-green-500 text-white px-1.5 py-0.5 rounded text-xs">
                        Edited
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Statistics</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Views</span>
                <span className="font-semibold text-gray-900">{carousel.view_count || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Downloads</span>
                <span className="font-semibold text-gray-900">{carousel.download_count || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Shares</span>
                <span className="font-semibold text-gray-900">{carousel.share_count || 0}</span>
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                <span className="text-sm text-gray-600">Created</span>
                <span className="text-sm text-gray-900">
                  {new Date(carousel.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Updated</span>
                <span className="text-sm text-gray-900">
                  {new Date(carousel.updated_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarouselDetailPage;
